import { Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BehaviorSubject, from, map } from 'rxjs';
import { Folder } from 'src/app/api/services/folder';
import { FoldersApiService } from 'src/app/api/services/folders-api.service';
import {
  FolderService,
  FolderTreeItem,
} from 'src/app/shared/services/folder.service';

@Injectable()
export class MoveFolderDialogService {
  private folders$: BehaviorSubject<FolderListItem[]> = new BehaviorSubject<
    FolderListItem[]
  >([]);
  private loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false,
  );
  private parentId = '';
  private folderTree$: BehaviorSubject<FolderTreeItem[]> = new BehaviorSubject<
    FolderTreeItem[]
  >(this.folderService.folderTree);

  folders = toSignal(this.folders$.asObservable());
  folderTree = toSignal(this.folderTree$.asObservable());
  loading = toSignal(this.loading$.asObservable());

  constructor(
    private foldersApiService: FoldersApiService,
    private folderService: FolderService,
  ) {}

  /**
   * Loads every folder of the current level. `loadFolders` drains all pages,
   * so the dialog is not limited to the first one, and it scopes the query by
   * parent — including the root level, which a raw `kind` query would not do.
   * Without that scope a page could be filled with folders from other levels
   * that `showFolderItem` then drops, hiding folders that do belong here.
   */
  updateFolderList(options: ListOptions) {
    const { kind, search, parentId } = options;

    this.loading$.next(true);
    from(
      this.folderService.loadFolders(kind, {
        query: search,
        includeSubcompanies: false,
        sortBy: 'name',
        reverse: false,
        folderId: parentId ?? '',
      }),
    )
      .pipe(
        map((items) =>
          items.filter(
            (item) =>
              search ||
              this.folderService.showFolderItem(
                parentId || this.foldersApiService.rootFolderId,
                item.parentId,
              ),
          ),
        ),
        map((items) =>
          items.map((item) => ({
            id: item.id,
            name: item.name,
            isSelected: false,
            isFolder: true,
          })),
        ),
      )
      .subscribe({
        next: (resp) => {
          this.folders$.next(resp);
          this.parentId = parentId ?? '';
        },
        error: (err) => {
          console.error(err);
          this.loading$.next(false);
        },
        complete: () => {
          this.loading$.next(false);
        },
      });
  }

  initTree(kind: 'Display' | 'Schedule' | 'Presentation') {
    from(
      this.folderService.loadFolders(kind, {
        query: '',
        includeSubcompanies: false,
        sortBy: 'name',
        reverse: false,
      }),
    ).subscribe({
      next: (resp) => {
        this.folderService.loadTree(resp);
        this.folderTree$.next(this.folderService.folderTree);
      },
    });
  }

  handleFolderSelection(folder: FolderListItem) {
    this.folders$.next(
      this.folders$.value.map((item) => ({
        ...item,
        isSelected: item.id === folder.id ? !item.isSelected : false,
      })),
    );
  }

  get currentParentFolderId() {
    return this.parentId;
  }
}

type ListOptions = {
  kind: 'Display' | 'Schedule' | 'Presentation';
  folderId: string;
  search?: string;
  parentId?: string;
};

export type FolderListItem = Pick<Folder, 'id' | 'name'> & {
  isSelected: boolean;
};
